import React, { useState, useEffect } from 'react';

const ModalReporteCliente = ({ isOpen, onClose, clientes, contenedores }) => {
  const [filtros, setFiltros] = useState({
    cliente: '',
    contenedorEspecifico: '',
    mostrarSoloFacturados: false,
    mostrarSoloPendientes: false,
    fechaDesde: '',
    fechaHasta: '',
    incluirCampos: {
      folio: true,
      master: true,
      contenedor: true,
      naviera: true,
      ata: true,
      ultimoDiaSinDemoras: true,
      fechaVacio: true,
      ventaSinIVA: true,
      ventaConIVA: true,
      ventaDemorasSinIVA: true,
      ventaDemorasConIVA: true,
      utilidad: true,
      diasDemoras: true,
      ultimaFactura: true,
      diasAFacturar: true,
      factura: true,
      hastaQueFecha: true,
      facturaPagoNaviera: true,
    }
  });

  const [contenedoresFiltrados, setContenedoresFiltrados] = useState([]);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const response = await fetch('/LogoDragon.png');
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoBase64(reader.result);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.log('Logo no encontrado');
      }
    };
    cargarLogo();
  }, []);

  useEffect(() => {
    if (filtros.cliente && !clientes.find(c => c.nombre === filtros.cliente)) {
      console.log('⚠️ Cliente eliminado detectado, limpiando selección');
      setFiltros(prev => ({ ...prev, cliente: '' }));
    }
  }, [clientes, filtros.cliente]);

  useEffect(() => {
    if (filtros.cliente) {
      let filtered = contenedores.filter(c => c.cliente === filtros.cliente);

      if (filtros.contenedorEspecifico && filtros.contenedorEspecifico.trim() !== '') {
        filtered = filtered.filter(c => 
          c.contenedor && c.contenedor.toLowerCase().includes(filtros.contenedorEspecifico.toLowerCase())
        );
      }

      if (filtros.mostrarSoloFacturados) {
        filtered = filtered.filter(c => c.factura === 'SI');
      } else if (filtros.mostrarSoloPendientes) {
        filtered = filtered.filter(c => c.factura === 'NO');
      }

      if (filtros.fechaDesde || filtros.fechaHasta) {
        filtered = filtered.filter(c => {
          if (!c.ata || c.ata === '') return false;

          try {
            const fechaAta = new Date(c.ata + 'T00:00:00');
            const desde = filtros.fechaDesde ? new Date(filtros.fechaDesde + 'T00:00:00') : null;
            const hasta = filtros.fechaHasta ? new Date(filtros.fechaHasta + 'T23:59:59') : null;

            if (desde && fechaAta < desde) return false;
            if (hasta && fechaAta > hasta) return false;
            return true;
          } catch (error) {
            console.error('Error al comparar fechas:', error);
            return false;
          }
        });
      }

      setContenedoresFiltrados(filtered);

      const totalCosto = filtered.reduce((sum, c) => sum + (parseFloat(c.costoNavieraConIVA) || 0), 0);
      const totalVenta = filtered.reduce((sum, c) => sum + (parseFloat(c.ventaClienteConIVA) || 0), 0);
      const totalDemoras = filtered.reduce((sum, c) => sum + (parseInt(c.diasDemoras) || 0), 0);
      const totalDiasAFacturar = filtered.reduce((sum, c) => sum + (parseInt(c.diasAFacturar) || 0), 0);
      const facturados = filtered.filter(c => c.factura === 'SI').length;
      const pendientes = filtered.filter(c => c.factura === 'NO').length;

      setVistaPrevia({
        total: filtered.length,
        facturados,
        pendientes,
        totalCosto,
        totalVenta,
        utilidad: totalVenta - totalCosto,
        totalDemoras,
        totalDiasAFacturar
      });
    } else {
      setContenedoresFiltrados([]);
      setVistaPrevia(null);
    }
  }, [filtros, contenedores]);

  const handleChangeFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleChangeCampo = (campo) => {
    setFiltros(prev => ({
      ...prev,
      incluirCampos: {
        ...prev.incluirCampos,
        [campo]: !prev.incluirCampos[campo]
      }
    }));
  };

  const toggleTodosCampos = (valor) => {
    const nuevosCampos = {};
    Object.keys(filtros.incluirCampos).forEach(campo => {
      nuevosCampos[campo] = valor;
    });
    setFiltros(prev => ({
      ...prev,
      incluirCampos: nuevosCampos
    }));
  };

  const generarPDFMejorado = async () => {
    if (!filtros.cliente || contenedoresFiltrados.length === 0) {
      alert('Por favor selecciona un cliente con contenedores');
      return;
    }

    setGenerandoPDF(true);

    try {
      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');

      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 20;

      // ===== HEADER BLANCO EJECUTIVO =====
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageWidth, 50, 'F');

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(1);
      doc.line(0, 50, pageWidth, 50);

      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', 8, 4, 35, 15);
        } catch (error) {
          console.log('Error al cargar logo');
        }
      }

      doc.setTextColor(17, 24, 39);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('REPORTE INDIVIDUAL DE CONTENEDOR', logoBase64 ? 75 : 15, 22);

      const fechaActual = new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const horaActual = new Date().toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit'
      });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(`${fechaActual}`, pageWidth - 15, 30, { align: 'right' });
      doc.text(horaActual, pageWidth - 15, 36, { align: 'right' });

      yPosition = 60;

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('CLIENTE:', 15, yPosition);
      
      doc.setFontSize(14);
      doc.setTextColor(30, 64, 175);
      doc.text(filtros.cliente.toUpperCase(), 40, yPosition);

      yPosition += 10;
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 10;

      if (filtros.contenedorEspecifico || filtros.mostrarSoloFacturados || filtros.mostrarSoloPendientes || filtros.fechaDesde || filtros.fechaHasta) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(127, 29, 29);
        doc.text('FILTROS APLICADOS:', 15, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);

        if (filtros.contenedorEspecifico) {
          doc.text(`Contenedor: ${filtros.contenedorEspecifico}`, 15, yPosition);
          yPosition += 5;
        }
        if (filtros.mostrarSoloFacturados) {
          doc.text('Solo Facturados', 15, yPosition);
          yPosition += 5;
        }
        if (filtros.mostrarSoloPendientes) {
          doc.text('Solo Pendientes', 15, yPosition);
          yPosition += 5;
        }
        if (filtros.fechaDesde) {
          doc.text(`Desde: ${filtros.fechaDesde}`, 15, yPosition);
          yPosition += 5;
        }
        if (filtros.fechaHasta) {
          doc.text(`Hasta: ${filtros.fechaHasta}`, 15, yPosition);
          yPosition += 5;
        }

        yPosition += 5;
        doc.setLineWidth(0.3);
        doc.line(15, yPosition, pageWidth - 15, yPosition);
        yPosition += 8;
      }

      doc.setFillColor(249, 250, 251);
      doc.roundedRect(15, yPosition, pageWidth - 30, 35, 3, 3, 'F');

      yPosition += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      doc.text('RESUMEN EJECUTIVO', 20, yPosition);

      yPosition += 8;

      const boxWidth = (pageWidth - 50) / 6;
      const startX = 20;

      const resumenCompacto = [
        { label: 'Total', value: vistaPrevia.total, color: [59, 130, 246] },
        { label: 'Facturados', value: vistaPrevia.facturados, color: [16, 185, 129] },
        { label: 'Pendientes', value: vistaPrevia.pendientes, color: [234, 88, 12] },
        { label: 'Demoras', value: vistaPrevia.totalDemoras, color: [220, 38, 38] },
        { label: 'Utilidad', value: `$${(vistaPrevia.utilidad / 1000).toFixed(1)}K`, color: vistaPrevia.utilidad >= 0 ? [6, 95, 70] : [153, 27, 27] }
      ];

      resumenCompacto.forEach((item, index) => {
        const x = startX + (index * boxWidth);

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text(item.label, x, yPosition);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(item.color[0], item.color[1], item.color[2]);
        doc.text(item.value.toString(), x, yPosition + 6);
      });

      yPosition += 20;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      doc.text('DETALLE DE CONTENEDORES', 15, yPosition);

      yPosition += 5;

      const columnas = [];
      const columnStyles = {};
      let colIndex = 0;

      const camposConfig = {
        folio: { label: 'Folio', width: 14, align: 'left' },
        master: { label: 'Master', width: 16, align: 'left' },
        contenedor: { label: 'Contenedor', width: 18, align: 'left' },
        naviera: { label: 'Naviera', width: 14, align: 'left' },
        ata: { label: 'ATA', width: 14, align: 'center' },
        ultimoDiaSinDemoras: { label: 'Ult.s/D', width: 14, align: 'center' },
        fechaVacio: { label: 'F.Vacio', width: 14, align: 'center' },
        ventaSinIVA: { label: 'Venta Base', width: 15, align: 'right' },
        ventaConIVA: { label: 'Venta Total', width: 15, align: 'right' },
        ventaDemorasSinIVA: { label: 'V.Dem Base', width: 15, align: 'right' },
        ventaDemorasConIVA: { label: 'V.Dem Total', width: 15, align: 'right' },
        utilidad: { label: 'Utilidad', width: 15, align: 'right' },
        diasDemoras: { label: 'Demor', width: 10, align: 'center' },
        ultimaFactura: { label: 'Ult.Fact', width: 14, align: 'center' },
        diasAFacturar: { label: 'D.Fac', width: 10, align: 'center' },
        factura: { label: 'Fact', width: 8, align: 'center' },
        hastaQueFecha: { label: 'Hasta', width: 14, align: 'center' },
        facturaPagoNaviera: { label: 'F.Pago', width: 16, align: 'left' }
      };

      Object.keys(filtros.incluirCampos).forEach(campo => {
        if (filtros.incluirCampos[campo] && camposConfig[campo]) {
          columnas.push(camposConfig[campo].label);
          columnStyles[colIndex] = {
            halign: camposConfig[campo].align,
            cellWidth: camposConfig[campo].width
          };
          colIndex++;
        }
      });

      const tableData = contenedoresFiltrados.map(c => {
        const row = [];
        
        Object.keys(filtros.incluirCampos).forEach(campo => {
          if (!filtros.incluirCampos[campo]) return;

          switch(campo) {
            case 'folio':
              row.push(c.folio || '-');
              break;
            case 'master':
              row.push(c.master || '-');
              break;
            case 'contenedor':
              row.push(c.contenedor || '-');
              break;
            case 'naviera':
              row.push(c.naviera || '-');
              break;
            case 'ata':
              row.push(c.ata || '-');
              break;
            case 'ultimoDiaSinDemoras':
              row.push(c.ultimoDiaSinDemoras || '-');
              break;
            case 'fechaVacio':
              row.push(c.fechaVacio || '-');
              break;
            case 'ventaSinIVA':
              row.push(`$${(parseFloat(c.ventaClienteSinIVA) || 0).toFixed(2)}`);
              break;
            case 'ventaConIVA':
              row.push(`$${(parseFloat(c.ventaClienteConIVA) || 0).toFixed(2)}`);
              break;
            case 'ventaDemorasSinIVA':
              row.push(`$${(parseFloat(c.ventaFacturacionSinIVA) || 0).toFixed(2)}`);
              break;
            case 'ventaDemorasConIVA':
              row.push(`$${(parseFloat(c.ventaFacturacionConIVA) || 0).toFixed(2)}`);
              break;
            case 'utilidad':
              const utilidad = (parseFloat(c.ventaClienteConIVA) || 0) - (parseFloat(c.costoNavieraConIVA) || 0);
              row.push(`$${utilidad.toFixed(2)}`);
              break;
            case 'diasDemoras':
              row.push((c.diasDemoras || 0).toString());
              break;
            case 'ultimaFactura':
              row.push(c.ultimaFactura || '-');
              break;
            case 'diasAFacturar':
              row.push((c.diasAFacturar || 0).toString());
              break;
            case 'factura':
              row.push(c.factura === 'SI' ? 'SI' : 'NO');
              break;
            case 'hastaQueFecha':
              row.push(c.hastaQueFecha || '-');
              break;
            case 'facturaPagoNaviera':
              const texto = c.facturaPagoNaviera || '-';
              row.push(texto.length > 20 ? texto.substring(0, 18) + '..' : texto);
              break;
          }
        });

        return row;
      });

      const footerRow = [];
      Object.keys(filtros.incluirCampos).forEach(campo => {
        if (!filtros.incluirCampos[campo]) return;

        switch(campo) {
          case 'folio':
            footerRow.push('TOTALES');
            break;
          case 'utilidad':
            footerRow.push(`$${vistaPrevia.utilidad.toFixed(2)}`);
            break;
          case 'diasDemoras':
            footerRow.push(vistaPrevia.totalDemoras.toString());
            break;
          case 'diasAFacturar':
            footerRow.push(vistaPrevia.totalDiasAFacturar.toString());
            break;
          case 'factura':
            footerRow.push(`${vistaPrevia.facturados}/${vistaPrevia.total}`);
            break;
          default:
            footerRow.push('');
        }
      });

      // Escala proporcional: si la suma de anchos excede el ancho disponible, se reduce uniformemente
      const anchoDisponible = pageWidth - 20;
      const sumAnchos = Object.values(columnStyles).reduce((sum, s) => sum + (s.cellWidth || 0), 0);
      if (sumAnchos > anchoDisponible) {
        const factor = anchoDisponible / sumAnchos;
        Object.keys(columnStyles).forEach(col => {
          columnStyles[col].cellWidth = columnStyles[col].cellWidth * factor;
        });
      }

      const numColumnas = columnas.length;
      const fontSize = numColumnas >= 14 ? 5.5 : numColumnas >= 9 ? 6 : 7;
      const cellPadding = numColumnas >= 14 ? 1 : numColumnas >= 9 ? 1.5 : 2;

      doc.autoTable({
        startY: yPosition,
        head: [columnas],
        body: tableData,
        foot: [footerRow],
        theme: 'striped',
        styles: {
          fontSize: fontSize,
          cellPadding: cellPadding,
          overflow: 'linebreak',
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [229, 231, 235]
        },
        headStyles: {
          fillColor: [17, 24, 39],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: fontSize,
          halign: 'center',
          cellPadding: cellPadding
        },
        footStyles: {
          fillColor: [31, 41, 55],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: fontSize,
          halign: 'center',
          cellPadding: cellPadding
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        columnStyles: columnStyles,
        margin: { left: 10, right: 10 },
        didParseCell: function(data) {
          const utilidadIndex = Object.keys(filtros.incluirCampos).filter(k => filtros.incluirCampos[k]).indexOf('utilidad');
          if (data.section === 'body' && data.column.index === utilidadIndex && utilidadIndex !== -1) {
            const valor = parseFloat(data.cell.raw.replace(/[$,]/g, ''));
            if (valor < 0) {
              data.cell.styles.textColor = [220, 38, 38];
              data.cell.styles.fontStyle = 'bold';
            } else {
              data.cell.styles.textColor = [5, 150, 105];
              data.cell.styles.fontStyle = 'bold';
            }
          }
          
          const facturaIndex = Object.keys(filtros.incluirCampos).filter(k => filtros.incluirCampos[k]).indexOf('factura');
          if (data.section === 'body' && data.column.index === facturaIndex && facturaIndex !== -1) {
            if (data.cell.raw === 'SI') {
              data.cell.styles.textColor = [5, 150, 105];
              data.cell.styles.fontStyle = 'bold';
            } else {
              data.cell.styles.textColor = [156, 163, 175];
            }
          }
        }
      });

      const totalPages = doc.internal.pages.length - 1;

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.5);
        doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
        
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128);
        doc.setFont('helvetica', 'normal');
        
        doc.text(
          `Dragon Cargo - ${fechaActual} ${horaActual}`,
          15,
          pageHeight - 10
        );
        
        doc.text(
          `Pagina ${i} de ${totalPages}`,
          pageWidth - 15,
          pageHeight - 10,
          { align: 'right' }
        );
      }

      const nombreArchivo = `Reporte_${filtros.cliente.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      doc.save(nombreArchivo);

      console.log(`✅ PDF generado: ${nombreArchivo}`);

    } catch (error) {
      console.error('❌ Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor intenta de nuevo.');
    } finally {
      setGenerandoPDF(false);
    }
  };

  const handleGenerarReporte = (formato) => {
    if (formato === 'pdf') {
      generarPDFMejorado();
      return;
    }

    if (!filtros.cliente) {
      alert('Por favor selecciona un cliente');
      return;
    }

    if (contenedoresFiltrados.length === 0) {
      alert('No hay contenedores que coincidan con los filtros aplicados');
      return;
    }

    const params = new URLSearchParams({
      formato,
      contenedor: filtros.contenedorEspecifico || '',
      soloFacturados: filtros.mostrarSoloFacturados ? 'true' : 'false',
      soloPendientes: filtros.mostrarSoloPendientes ? 'true' : 'false',
      fechaDesde: filtros.fechaDesde || '',
      fechaHasta: filtros.fechaHasta || '',
      campos: JSON.stringify(filtros.incluirCampos)
    });

    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/reportes/cliente/${encodeURIComponent(filtros.cliente)}/detallado?${params}`;
    
    console.log('🔄 Generando reporte:', {
      cliente: filtros.cliente,
      formato,
      contenedoresFiltrados: contenedoresFiltrados.length,
      url
    });

    window.open(url, '_blank');
  };

  const limpiarFiltros = () => {
    setFiltros({
      cliente: '',
      contenedorEspecifico: '',
      mostrarSoloFacturados: false,
      mostrarSoloPendientes: false,
      fechaDesde: '',
      fechaHasta: '',
      incluirCampos: {
        folio: true,
        master: true,
        contenedor: true,
        naviera: true,
        ata: true,
        ultimoDiaSinDemoras: true,
        fechaVacio: true,
        ventaSinIVA: true,
        ventaConIVA: true,
        ventaDemorasSinIVA: true,
        ventaDemorasConIVA: true,
        utilidad: true,
        diasDemoras: true,
        ultimaFactura: true,
        diasAFacturar: true,
        factura: true,
        hastaQueFecha: true,
        facturaPagoNaviera: true,
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        
        <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-8 py-6 border-b-4 border-blue-500">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500 p-3 rounded-xl shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Generador de Reportes</h2>
                <p className="text-slate-300 text-sm mt-1">Configure los filtros y campos para su reporte personalizado</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl p-2.5 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="max-w-6xl mx-auto space-y-6">
          
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h3 className="text-lg font-semibold text-white">Seleccionar Cliente</h3>
                <div className="ml-auto bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg">
                  <span className="text-white text-sm font-medium">{clientes.length} clientes disponibles</span>
                </div>
              </div>
              <div className="p-6">
                <select 
                  value={filtros.cliente}
                  onChange={(e) => handleChangeFiltro('cliente', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium text-slate-700"
                >
                  <option value="">-- Seleccione un cliente (o escriba para buscar) --</option>
                  {clientes.length > 0 ? (
                    clientes.map((cliente) => (
                      <option key={cliente.nombre} value={cliente.nombre}>
                        {cliente.nombre} ({cliente.totalContenedores} contenedores)
                      </option>
                    ))
                  ) : (
                    <option disabled>No hay clientes disponibles</option>
                  )}
                </select>

                {clientes.length === 0 && (
                  <div className="text-sm text-amber-600 bg-amber-50 px-4 py-3 rounded-lg border border-amber-200 flex items-center gap-2 mt-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>No hay clientes con contenedores registrados</span>
                  </div>
                )}
              </div>
            </div>

            {filtros.cliente && (
              <>
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                      <span className="text-white font-bold text-lg">2</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Filtros Avanzados</h3>
                    {contenedoresFiltrados.length > 0 && (
                      <div className="ml-auto bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg">
                        <span className="text-white text-sm font-medium">
                          {contenedoresFiltrados.length} contenedor(es) encontrado(s)
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          Buscar Contenedor
                        </label>
                        <input
                          type="text"
                          value={filtros.contenedorEspecifico}
                          onChange={(e) => handleChangeFiltro('contenedorEspecifico', e.target.value)}
                          placeholder="Ej: MSKU1234567"
                          className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                        {filtros.contenedorEspecifico && (
                          <p className="text-xs text-purple-600 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Filtrando por: "{filtros.contenedorEspecifico}"
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Fecha Desde
                        </label>
                        <input
                          type="date"
                          value={filtros.fechaDesde}
                          onChange={(e) => handleChangeFiltro('fechaDesde', e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Fecha Hasta
                        </label>
                        <input
                          type="date"
                          value={filtros.fechaHasta}
                          onChange={(e) => handleChangeFiltro('fechaHasta', e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border-2 border-purple-200">
                      <label className="block text-sm font-semibold text-slate-700 mb-3">Estado de Facturación</label>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-3 px-5 py-3 bg-white rounded-lg border-2 border-slate-200 hover:border-purple-400 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={filtros.mostrarSoloFacturados}
                            onChange={(e) => {
                              handleChangeFiltro('mostrarSoloFacturados', e.target.checked);
                              if (e.target.checked) handleChangeFiltro('mostrarSoloPendientes', false);
                            }}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="font-medium text-slate-700">✓ Solo Facturados</span>
                        </label>
                        <label className="flex items-center gap-3 px-5 py-3 bg-white rounded-lg border-2 border-slate-200 hover:border-purple-400 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={filtros.mostrarSoloPendientes}
                            onChange={(e) => {
                              handleChangeFiltro('mostrarSoloPendientes', e.target.checked);
                              if (e.target.checked) handleChangeFiltro('mostrarSoloFacturados', false);
                            }}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="font-medium text-slate-700">⚠ Solo Pendientes</span>
                        </label>
                      </div>
                    </div>

                    {(filtros.contenedorEspecifico || filtros.mostrarSoloFacturados || filtros.mostrarSoloPendientes || filtros.fechaDesde || filtros.fechaHasta) && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-bold text-blue-900">Filtros Activos:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {filtros.contenedorEspecifico && (
                            <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-blue-700 border border-blue-300">
                              Contenedor: {filtros.contenedorEspecifico}
                            </span>
                          )}
                          {filtros.mostrarSoloFacturados && (
                            <span className="px-3 py-1 bg-green-100 rounded-full text-xs font-medium text-green-700 border border-green-300">
                              ✓ Solo Facturados
                            </span>
                          )}
                          {filtros.mostrarSoloPendientes && (
                            <span className="px-3 py-1 bg-amber-100 rounded-full text-xs font-medium text-amber-700 border border-amber-300">
                              ⚠ Solo Pendientes
                            </span>
                          )}
                          {filtros.fechaDesde && (
                            <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-slate-700 border border-slate-300">
                              Desde: {filtros.fechaDesde}
                            </span>
                          )}
                          {filtros.fechaHasta && (
                            <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-slate-700 border border-slate-300">
                              Hasta: {filtros.fechaHasta}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                        <span className="text-white font-bold text-lg">3</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white">Campos a Incluir en el Reporte</h3>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleTodosCampos(true)}
                        className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all text-sm font-semibold backdrop-blur-sm"
                      >
                        ✓ Todos
                      </button>
                      <button
                        onClick={() => toggleTodosCampos(false)}
                        className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all text-sm font-semibold backdrop-blur-sm"
                      >
                        ✗ Ninguno
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-6">
                      <h4 className="font-bold text-slate-700 mb-4 pb-2 border-b-2 border-emerald-200 flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        Información Básica
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['folio', 'master', 'contenedor', 'naviera'].map(campo => (
                          <label key={campo} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg hover:bg-emerald-50 cursor-pointer transition-all border-2 border-transparent hover:border-emerald-200">
                            <input
                              type="checkbox"
                              checked={filtros.incluirCampos[campo]}
                              onChange={() => handleChangeCampo(campo)}
                              className="w-4 h-4 text-emerald-600 rounded"
                            />
                            <span className="text-sm font-medium text-slate-700 capitalize">{campo}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-700 mb-4 pb-2 border-b-2 border-blue-200 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Fechas
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'ata', label: 'ATA' },
                          { key: 'ultimoDiaSinDemoras', label: 'Último Día Sin Demoras' },
                          { key: 'fechaVacio', label: 'Fecha de Vacío' }
                        ].map(campo => (
                          <label key={campo.key} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-all border-2 border-transparent hover:border-blue-200">
                            <input
                              type="checkbox"
                              checked={filtros.incluirCampos[campo.key]}
                              onChange={() => handleChangeCampo(campo.key)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span className="text-sm font-medium text-slate-700">{campo.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-700 mb-4 pb-2 border-b-2 border-orange-200 flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        Días
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'diasDemoras', label: 'Días de Demoras' },
                          { key: 'diasAFacturar', label: 'Días a Facturar' }
                        ].map(campo => (
                          <label key={campo.key} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg hover:bg-orange-50 cursor-pointer transition-all border-2 border-transparent hover:border-orange-200">
                            <input
                              type="checkbox"
                              checked={filtros.incluirCampos[campo.key]}
                              onChange={() => handleChangeCampo(campo.key)}
                              className="w-4 h-4 text-orange-600 rounded"
                            />
                            <span className="text-sm font-medium text-slate-700">{campo.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-700 mb-4 pb-2 border-b-2 border-green-200 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Montos
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'utilidad', label: 'Utilidad' }
                        ].map(campo => (
                          <label key={campo.key} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg hover:bg-green-50 cursor-pointer transition-all border-2 border-transparent hover:border-green-200">
                            <input
                              type="checkbox"
                              checked={filtros.incluirCampos[campo.key]}
                              onChange={() => handleChangeCampo(campo.key)}
                              className="w-4 h-4 text-green-600 rounded"
                            />
                            <span className="text-sm font-medium text-slate-700">{campo.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold text-slate-700 mb-4 pb-2 border-b-2 border-purple-200 flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        Facturación
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'factura', label: 'Estado de Factura' },
                          { key: 'ultimaFactura', label: 'Última Factura' },
                          { key: 'hastaQueFecha', label: 'Hasta Qué Fecha' },
                          { key: 'facturaPagoNaviera', label: 'Factura y Pago Naviera' },
                          { key: 'ventaSinIVA', label: 'Venta Cliente (Base)' },
                          { key: 'ventaConIVA', label: 'Venta Cliente (Total)' },
                          { key: 'ventaDemorasSinIVA', label: 'Venta Demoras (Base)' },
                          { key: 'ventaDemorasConIVA', label: 'Venta Demoras (Total)' }
                        ].map(campo => (
                          <label key={campo.key} className="flex items-center gap-3 px-4 py-3 bg-purple-50 rounded-lg hover:bg-purple-100 cursor-pointer transition-all border-2 border-purple-200 hover:border-purple-400">
                            <input
                              type="checkbox"
                              checked={filtros.incluirCampos[campo.key]}
                              onChange={() => handleChangeCampo(campo.key)}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="text-sm font-semibold text-purple-900">{campo.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {vistaPrevia && (
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl p-6 border-4 border-blue-500">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-blue-500 p-2 rounded-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-white">Vista Previa del Reporte</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-xl shadow-lg">
                        <div className="text-xs text-blue-100 font-medium mb-1">Total Contenedores</div>
                        <div className="text-3xl font-bold text-white">{vistaPrevia.total}</div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-xl shadow-lg">
                        <div className="text-xs text-green-100 font-medium mb-1">Facturados</div>
                        <div className="text-3xl font-bold text-white">{vistaPrevia.facturados}</div>
                      </div>
                      <div className="bg-gradient-to-br from-red-500 to-red-600 p-5 rounded-xl shadow-lg">
                        <div className="text-xs text-red-100 font-medium mb-1">Pendientes</div>
                        <div className="text-3xl font-bold text-white">{vistaPrevia.pendientes}</div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-5 rounded-xl shadow-lg">
                        <div className="text-xs text-purple-100 font-medium mb-1">Total Días</div>
                        <div className="text-3xl font-bold text-white">{vistaPrevia.totalDias}</div>
                      </div>
                      <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-5 rounded-xl shadow-lg">
                        <div className="text-xs text-teal-100 font-medium mb-1">Utilidad Total</div>
                        <div className="text-3xl font-bold text-white">${vistaPrevia.utilidad.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}

          </div>
        </div>

        <div className="bg-white border-t-2 border-slate-200 px-8 py-5 flex justify-between items-center shadow-lg">
          <button
            onClick={limpiarFiltros}
            className="px-6 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all font-medium"
          >
            🔄 Limpiar Filtros
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-all font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={() => handleGenerarReporte('excel')}
              disabled={!filtros.cliente || contenedoresFiltrados.length === 0}
              className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-lg disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed font-semibold"
            >
              📊 Exportar Excel
            </button>
            <button
              onClick={() => handleGenerarReporte('pdf')}
              disabled={!filtros.cliente || contenedoresFiltrados.length === 0 || generandoPDF}
              className="px-8 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-lg disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed font-semibold"
            >
              {generandoPDF ? '⏳ Generando...' : '📄 Exportar PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalReporteCliente;
